import { useUserData } from './useUserData';
import { executeApiRequest } from '~/utils/api';

export const useBlackrollAuth = () => {
  const { user, loggedIn, fetch, clear, session } = useUserSession();

  // Login with email and password
  const login = async (email, password, languageCode) => {
    return await executeApiRequest(async () => {
      const { status, error } = await useFetch('/api/auth/login', {
        method: 'POST',
        body: { email, password, languageCode },
      });

      if (status.value === 'error') {
        throw error.value;
      }

      // Retry session fetch with exponential backoff
      let retries = 3;
      let delay = 100;

      while (retries > 0) {
        await fetch();

        // Check if session has valid data
        if (session.value?.idToken && session.value?.user) {
          break;
        }

        if (retries > 1) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          delay *= 2;
        }

        retries--;
      }

      return { success: true };
    }, 'Authentication failed');
  };

  // Register a new user
  const register = async (userData) => {
    return await executeApiRequest(async () => {
      const { status, error } = await useFetch('/api/auth/register', {
        method: 'POST',
        body: userData,
      });

      if (status.value === 'error') {
        throw error.value;
      }

      // This updates the session data
      await fetch();

      return { success: true };
    }, 'Registration failed');
  };

  // Reset password
  const resetPassword = async (email, languageCode) => {
    return await executeApiRequest(async () => {
      const { status, error } = await useFetch('/api/auth/resetPassword', {
        method: 'POST',
        body: { email, languageCode },
      });

      if (status.value === 'error') {
        throw error.value;
      }

      return { success: true };
    }, 'Password reset failed');
  };

  // Set password
  const setPassword = async (code, password) => {
    return await executeApiRequest(async () => {
      const { status, error } = await useFetch('/api/auth/setPassword', {
        method: 'POST',
        body: {
          code,
          password,
        },
      });

      if (status.value === 'error') {
        throw error.value;
      }

      return { success: true };
    }, 'Set new password failed');
  };

  // Log out the user
  const logout = async () => {
    return await executeApiRequest(async () => {
      // Get the userData composable to clear user data
      const { clearUserData } = useUserData();

      const { status, error } = await useFetch('/api/auth/logout', {
        method: 'POST',
      });

      if (status.value === 'error') {
        throw error.value;
      }

      // Clear user data in the composable store
      clearUserData();

      // This updates the session data
      await clear();

      return { success: true };
    }, 'Logout failed');
  };

  // Add addon code
  const addAddon = async (code) => {
    return await executeApiRequest(async () => {
      // Call the API using the server endpoint
      const { status, error, data } = await useFetch('/api/user/addon', {
        method: 'POST',
        body: { code },
      });

      if (status.value === 'error') {
        throw error.value;
      }

      // After successful redemption, refresh user data with force=true to bypass cache
      await fetch();

      // Format the response to match the previous structure for backward compatibility
      return { data: { data: { redeemCode: data.value } } };
    }, 'Failed to redeem code');
  };

  // Get Shopify Multipass token
  const getMultipass = async (language, returnUrl) => {
    return await executeApiRequest(async () => {
      const { status, error, data } = await useFetch('/api/auth/multipass', {
        method: 'POST',
        body: { language, returnUrl },
      });

      if (status.value === 'error') {
        throw error.value;
      }

      return {
        token: data.value.token,
      };
    }, 'Multipass generation failed');
  };

  // Resend verification email
  const resendVerification = async () => {
    return await executeApiRequest(async () => {
      const { status, error } = await useFetch('/api/auth/resendVerification', {
        method: 'POST',
      });

      if (status.value === 'error') {
        throw error.value;
      }

      return { success: true };
    }, 'Failed to resend verification email');
  };

  // Confirm email verification
  const confirmEmail = async (verificationCode) => {
    return await executeApiRequest(async () => {
      // Use $fetch directly instead of useFetch to avoid status issues
      try {
        const result = await $fetch('/api/auth/confirmEmail', {
          method: 'POST',
          body: { verificationCode },
        });

        // This updates the session data
        await fetch();

        return { success: true, data: result?.data };
      } catch (fetchError) {
        throw fetchError;
      }
    }, 'Email verification failed');
  };

  // Delete user account
  const deleteAccount = async () => {
    return await executeApiRequest(async () => {
      const { status, error, data } = await useFetch('/api/auth/deleteAccount', {
        method: 'POST',
      });

      if (status.value === 'error') {
        throw error.value;
      }

      // Clear user data
      const { clearUserData } = useUserData();
      clearUserData();

      // Clear the session
      await clear();

      return { success: true };
    }, 'Account deletion failed');
  };

  // Update user profile
  const updateUserProfile = async (profileData) => {
    return await executeApiRequest(async () => {
      const { status, error, data } = await useFetch('/api/user/profile', {
        method: 'PUT',
        body: profileData,
      });

      if (status.value === 'error') {
        throw error.value;
      }

      // Refresh session data
      await fetch();

      return data.value;
    }, 'Profile update failed');
  };

  return {
    // State
    user,
    loggedIn,
    session,

    // Methods
    login,
    register,
    resetPassword,
    setPassword,
    logout,
    fetch,
    clear,
    addAddon,
    getMultipass,
    resendVerification,
    confirmEmail,
    deleteAccount,
    updateUserProfile,
  };
};
